/**
 * Presence — who is active right now.
 *
 * Always resolved per viewer on the server. There is no client-side visibility decision to make:
 * anyone hiding, blocked, or not a friend arrives as Offline with no timestamp, identical to
 * somebody genuinely away. That sameness is the feature — being able to tell "hidden" from
 * "offline" would announce that a person is hiding.
 */

/** Mirrors the server's UserStatus enum. */
export enum PresenceState {
  Online = 0,
  Away = 1,
  Busy = 2,
  /** Away, hiding, or none of your business — indistinguishable by design. */
  Offline = 3,
}

export interface UserPresenceState {
  userId: string
  state: PresenceState
  /** Null whenever state is Offline. */
  lastSeenAt: string | null
}

/** What the client is doing — not what the user chose. Only these two are ever sent. */
export type PresenceActivity = PresenceState.Online | PresenceState.Away

export interface PresenceHeartbeatResponse {
  serverTime: string
  users: UserPresenceState[]
}

/**
 * What to call a state in the UI.
 *
 * Offline stays a single word for all three of its meanings — away, appearing offline, or not a
 * friend. The server refuses to distinguish them, and a label like "Hidden" would undo that.
 *
 * Away and Busy are shown as themselves. Folding them into "Online" (which the chat header used
 * to do) meant a friend switching to Busy produced no visible change at all, so presence looked
 * frozen: the only transition that could ever alter the label was going offline entirely.
 */
export function presenceLabel(state: PresenceState): string {
  switch (state) {
    case PresenceState.Online:
      return 'Online'
    case PresenceState.Away:
      return 'Away'
    case PresenceState.Busy:
      return 'Busy'
    default:
      return 'Offline'
  }
}

/** Theme token for the status dot. Shared so the account menu and chat cannot drift apart. */
export function presenceColor(state: PresenceState): string {
  switch (state) {
    case PresenceState.Online:
      return 'success.main'
    case PresenceState.Away:
      return 'warning.main'
    case PresenceState.Busy:
      return 'error.main'
    default:
      // Appearing offline is a normal choice, not an error state — keep it quiet.
      return 'action.disabled'
  }
}
